/**
 * Mesh edit mode state mirrored from the engine (`meshEdit` events) and the
 * modeling viewport options sent back with `setOption('meshEdit', json)`.
 * Pure helpers so the parsing and option merging are testable.
 */

export type MeshMode = 'vertex' | 'edge' | 'face';

export type Falloff = 'smooth' | 'sphere' | 'root' | 'inverseSquare' | 'sharp' | 'linear' | 'constant' | 'random';
export type SnapElement = 'increment' | 'vertex' | 'edge' | 'face' | 'edgeMidpoint';
export type SnapBase = 'closest' | 'center' | 'median' | 'active';
export type Matcap = '' | 'clay' | 'metal' | 'jade' | 'wax' | 'toon' | 'normal';

/** Mirrors `MeshOptions` in `prism_runtime/src/runtime/mesh_view.rs`. */
export interface MeshOptions {
  proportional: boolean;
  falloff: Falloff;
  radius: number;
  connected: boolean;
  snap: boolean;
  snapElements: SnapElement[];
  snapBase: SnapBase;
  alignRotation: boolean;
  increment: number;
  mirror: [boolean, boolean, boolean];
  topologyMirror: boolean;
  knifeCutThrough: boolean;
  knifeAngleSnap: boolean;
  knifeAngle: number;
  faceNormals: boolean;
  vertexNormals: boolean;
  normalLength: number;
  faceOrientation: boolean;
  stats: boolean;
  edgeLength: boolean;
  edgeAngle: boolean;
  faceArea: boolean;
  xray: boolean;
  xrayAlpha: number;
  matcap: Matcap;
  quad: boolean;
}

export const DEFAULT_MESH_OPTIONS: MeshOptions = {
  proportional: false,
  falloff: 'smooth',
  radius: 1,
  connected: false,
  snap: false,
  snapElements: ['increment'],
  snapBase: 'closest',
  alignRotation: false,
  increment: 0.1,
  mirror: [false, false, false],
  topologyMirror: false,
  knifeCutThrough: false,
  knifeAngleSnap: false,
  knifeAngle: 15,
  faceNormals: false,
  vertexNormals: false,
  normalLength: 0.1,
  faceOrientation: false,
  stats: true,
  edgeLength: false,
  edgeAngle: false,
  faceArea: false,
  xray: false,
  xrayAlpha: 0.35,
  matcap: '',
  quad: false,
};

export const FALLOFFS: Array<{ id: Falloff; label: string }> = [
  { id: 'smooth', label: 'Smooth' },
  { id: 'sphere', label: 'Sphere' },
  { id: 'root', label: 'Root' },
  { id: 'inverseSquare', label: 'Inverse Square' },
  { id: 'sharp', label: 'Sharp' },
  { id: 'linear', label: 'Linear' },
  { id: 'constant', label: 'Constant' },
  { id: 'random', label: 'Random' },
];

export const MATCAPS: Array<{ id: Matcap; label: string }> = [
  { id: '', label: 'Materials' },
  { id: 'clay', label: 'Clay' },
  { id: 'metal', label: 'Metal' },
  { id: 'jade', label: 'Jade' },
  { id: 'wax', label: 'Wax' },
  { id: 'toon', label: 'Toon' },
  { id: 'normal', label: 'Normals' },
];

export interface MeshStats {
  verts: number;
  edges: number;
  faces: number;
  tris: number;
  selVerts: number;
  selEdges: number;
  selFaces: number;
}

/** An interactive tool in progress (header hints and live values). */
export interface MeshModal {
  tool: string;
  hint: string;
  [key: string]: unknown;
}

/** The last operation (Adjust Last Operation panel): `name` plus its parameters. */
export interface MeshLastOp {
  name: string;
  [key: string]: unknown;
}

export interface MeshEditState {
  active: boolean;
  mode: MeshMode;
  /** Face mode (kept for older callers). */
  faces: boolean;
  selected: number;
  stats: MeshStats | null;
  modal: MeshModal | null;
  last: MeshLastOp | null;
  status: string | null;
}

export const INACTIVE_MESH_EDIT: MeshEditState = {
  active: false,
  mode: 'vertex',
  faces: false,
  selected: 0,
  stats: null,
  modal: null,
  last: null,
  status: null,
};

export interface MeshLabel {
  x: number;
  y: number;
  text: string;
}

const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);

function parseStats(raw: unknown): MeshStats | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    verts: num(r.verts),
    edges: num(r.edges),
    faces: num(r.faces),
    tris: num(r.tris),
    selVerts: num(r.sel_verts ?? r.selVerts),
    selEdges: num(r.sel_edges ?? r.selEdges),
    selFaces: num(r.sel_faces ?? r.selFaces),
  };
}

/** Engine `meshEdit` event payload → state. */
export function parseMeshEvent(event: Record<string, unknown>): MeshEditState {
  const mode: MeshMode = event.mode === 'edge' || event.mode === 'face' ? event.mode : event.faces ? 'face' : 'vertex';
  const modal = event.modal && typeof event.modal === 'object' ? (event.modal as MeshModal) : null;
  const last = event.last && typeof event.last === 'object' ? (event.last as MeshLastOp) : null;
  return {
    active: Boolean(event.active),
    mode,
    faces: mode === 'face',
    selected: num(event.selected),
    stats: parseStats(event.stats),
    modal,
    last,
    status: typeof event.status === 'string' ? event.status : null,
  };
}

/** Engine options (from an event) merged over defaults, ignoring unknown fields. */
export function parseMeshOptions(raw: unknown, base: MeshOptions = DEFAULT_MESH_OPTIONS): MeshOptions {
  if (!raw || typeof raw !== 'object') return base;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (k in base && typeof v === typeof (base as unknown as Record<string, unknown>)[k]) out[k] = v;
  }
  return out as unknown as MeshOptions;
}

/** Engine `meshLabels` payload → labels in CSS pixels. */
export function parseMeshLabels(raw: unknown, dpr = 1): MeshLabel[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((l): l is Record<string, unknown> => Boolean(l) && typeof l === 'object')
    .map((l) => ({ x: num(l.x) / dpr, y: num(l.y) / dpr, text: String(l.text ?? '') }));
}

/** Short statistics line like Blender's overlay. */
export function statsLines(stats: MeshStats, mode: MeshMode): string[] {
  const sel = (n: number, total: number, on: boolean) => (on ? `${n}/${total}` : `${total}`);
  return [
    `Vertices ${sel(stats.selVerts, stats.verts, mode === 'vertex')}`,
    `Edges ${sel(stats.selEdges, stats.edges, mode === 'edge')}`,
    `Faces ${sel(stats.selFaces, stats.faces, mode === 'face')}`,
    `Triangles ${stats.tris}`,
  ];
}
